import { LocationTarget } from '@bingads-webui-react/bing-maps';

/**
 * Function to format the matched location values as targets with name and isTargeted flag.
 * @param {Array} locations -  Array of objects containing location data and SearchTerm.
 * @returns {Array} - Returns an arry of objects with the target name and isTargeted.
 */

export const getFormattedMatchedValues = locations =>
  locations.map(loc => ({
    target: {
      name: loc.Location.ExpandedName,
      isTargeted: true,
      Location: loc.Location,
      SearchTerm: loc.SearchTerm,
    },
  }));

/**
 * Function to format the location values as Included LocationTarget objects
 * @param {Array} locations -  Array of target objects containing name, isTargeted, Location, and SearchTerm data.
 * @returns {Array} - Returns an arry of Included LocationTarget objects.
 */

export const formatIncludedLocationTargets = locations =>
  locations.map(loc => (LocationTarget.CreateIncludedLocationTarget(loc.target.Location)));

/**
 * Function to format the location values as Excluded LocationTarget objects
 * @param {Array} locations -  Array of target objects containing name, isTargeted, Location, and SearchTerm data.
 * @returns {Array} - Returns an arry of Excluded LocationTarget objects.
 */

export const formatExcludedLocationTargets = locations =>
  locations.map(loc => (LocationTarget.CreateExcludedLocationTarget(loc.target.Location)));

/**
 * Function to deduplicate bulk locations search APi response values and filter out duplicates already present in the matchedLocations and unmatchedLocations.
 * @param {Array} responseValues -  Array of locations returned from the bulk locations search API.
 * @param {Array} currentMatchedLocations -  Array of existiing matched locations formatted using getFormattedMatchedValues.
 * @param {Array} currentUnmatchedSearchResults -  Array of existiing unmatched search teams strings that were not matched by API.
 * @returns {Array} - Returns an arry of locations without duplicated entries
 */
export const getDedupedNewSearchResults = (responseValues, currentMatchedLocations, currentUnmatchedSearchResults) => {
  const existingLocationIds = new Set(currentMatchedLocations.map(item => item.target.Location.Id));
  const existingUnmatchedTerms = new Set(currentUnmatchedSearchResults);

  const { uniqueNewResults } = responseValues.reduce(
    (acc, newResult) => {
      if (newResult.Location) {
        const locId = newResult.Location.Id;
        if (!acc.seenMatchedIds.has(locId)) {
          acc.seenMatchedIds.add(locId);
          acc.uniqueNewResults.push(newResult);
        }
      } else {
        const term = newResult.SearchTerm;
        if (!acc.seenUnmatchedTerms.has(term)) {
          acc.seenUnmatchedTerms.add(term);
          acc.uniqueNewResults.push(newResult);
        }
      }
      return acc;
    },
    {
      uniqueNewResults: [],
      seenMatchedIds: new Set(existingLocationIds),
      seenUnmatchedTerms: new Set(existingUnmatchedTerms),
    }
  );

  return uniqueNewResults;
};
